/* =========================================================================
   JOMIKUTO · variant-selects.js
   <jomi-variant-selects> custom element.
   Listens for radio/select changes, finds matching variant, updates:
     - price display (via data-jomi-price target)
     - main media (via data-jomi-main-media target)
     - product form hidden id input
     - URL with ?variant=<id>
     - add-to-cart button state (sold out / available)
   ========================================================================= */

class JomiVariantSelects extends HTMLElement {
  connectedCallback() {
    this.variantData = this.#parseVariants();
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;

    this.addEventListener('change', this.onChange.bind(this));
    // Set initial state from checked inputs
    this.currentVariant = this.#findMatchingVariant();
  }

  #parseVariants() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return [];
    try { return JSON.parse(script.textContent); }
    catch { return []; }
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('input:checked, select option:checked'))
      .map(el => el.value);
  }

  #findMatchingVariant() {
    const selected = this.selectedOptionValues;
    return this.variantData.find(v => {
      return v.options.every((opt, i) => opt === selected[i]);
    });
  }

  onChange() {
    const variant = this.#findMatchingVariant();
    this.currentVariant = variant;
    if (!variant) return;

    this.#updateFormInputs(variant);
    this.#updatePrice(variant);
    this.#updateURL(variant);
    this.#updateAddButton(variant);
    this.#updateMedia(variant);

    // Broadcast so other components can react
    Jomi.bus.emit('variant:changed', { variant, sectionId: this.sectionId });
  }

  #updateFormInputs(variant) {
    // Find product form(s) in the same section and update hidden id
    const section = document.getElementById(`Section-${this.sectionId}`) || document;
    section.querySelectorAll(`input[name="id"][data-jomi-variant-id]`).forEach(input => {
      input.value = variant.id;
      input.disabled = !variant.available;
    });
  }

  #updatePrice(variant) {
    const priceEl = document.querySelector(`[data-jomi-price="${this.sectionId}"]`);
    if (!priceEl) return;

    const formatMoney = (cents) => {
      return Shopify && Shopify.formatMoney
        ? Shopify.formatMoney(cents, '${{amount}}')
        : `$${(cents / 100).toFixed(2)}`;
    };

    let html = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += `<span class="price-compare">${formatMoney(variant.compare_at_price)}</span>`;
      html += `<span class="price-sale">${formatMoney(variant.price)}</span>`;
    } else {
      html += `<span class="price-current">${formatMoney(variant.price)}</span>`;
    }
    priceEl.innerHTML = html;
  }

  #updateURL(variant) {
    if (!this.dataset.updateUrl || this.dataset.updateUrl === 'false') return;
    if (!variant || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  #updateAddButton(variant) {
    const button = document.querySelector(`[data-jomi-atc="${this.sectionId}"]`);
    if (!button) return;
    const label = button.querySelector('[data-jomi-atc-label]') || button;
    if (variant.available) {
      button.removeAttribute('disabled');
      label.textContent = button.dataset.labelAvailable || 'Add to bag';
    } else {
      button.setAttribute('disabled', '');
      label.textContent = button.dataset.labelSoldOut || 'Sold out';
    }
  }

  #updateMedia(variant) {
    if (!variant.featured_image) return;
    const main = document.querySelector(`[data-jomi-main-media="${this.sectionId}"]`);
    if (main && main.tagName === 'IMG') {
      main.src = variant.featured_image.src || variant.featured_image;
    }
  }
}

if (!customElements.get('jomi-variant-selects')) {
  customElements.define('jomi-variant-selects', JomiVariantSelects);
}
